<template>
  <component
    :is="componentTag"
    class="sp-tile"
    :class="classModifiers"
    :href="href"
    :target="target"
    :aria-label="ariaLabel"
    :tabindex="tabIndex"
    :rel="relAttribute"
    part="root"
  >
    <div v-if="icon" class="sp-tile__icon-wrapper" part="icon-wrapper">
      <div
        v-if="hasIconBackground"
        class="sp-tile__icon-background"
        :class="iconBackgroundClasses"
        :style="iconBackgroundStyle"
        part="icon-background"
      >
        <sp-icon :name="icon" :size="size" :size-sm="sizeSm" :fill-color="iconFillColor"></sp-icon>
      </div>
      <sp-icon v-else :name="icon" :size="size" :size-sm="sizeSm" :fill-color="iconFillColor"></sp-icon>
    </div>

    <div class="sp-tile__content" part="content">
      <span v-if="label" class="sp-tile__label" :class="labelClasses" part="label">
        <slot name="label">{{ label }}</slot>
      </span>

      <slot name="content" />
    </div>
  </component>
</template>

<script setup>
import { computed, useAttrs } from "vue";
import { useBreakpoints } from "#ce/composables/useBreakpoints";
import { toSystemColorVariable } from "#ce/composables/color";
import { toBoolean } from "#ce/utils/props";
import { useSize } from "#ce/composables/useSize";
import { useTypography } from "#ce/composables/useTypography";

import { props as propDefs } from "./props";

const props = defineProps(propDefs);

const attrs = useAttrs();

const { isSmScreen, breakpointModifiers } = useBreakpoints();
const { sizeModifiers } = useSize(props);
const { typographyModifiers } = useTypography(props);

const hasHref = computed(() => !!props.href);
const hasIconBackground = computed(() => !!props.iconBackgroundColor);

const componentTag = computed(() => (hasHref.value ? "a" : "div"));

const iconFillColor = computed(() => toSystemColorVariable(props.iconColor));

const iconBgColor = computed(() => toSystemColorVariable(props.iconBackgroundColor));

const iconBackgroundStyle = computed(() => ({
  "--icon-bg-color": iconBgColor.value,
}));

const ariaLabel = computed(() => (hasHref.value ? props.label : undefined));

const relAttribute = computed(() => (props.target === "_blank" ? "noopener noreferrer" : undefined));

const tabIndex = computed(() => (hasHref.value ? 0 : undefined));

const isRoleButton = computed(() => attrs.role === "button");
const isClickable = computed(() => hasHref.value || isRoleButton.value);

const isCard = computed(() => toBoolean(props.card));

const effectiveLayout = computed(() => {
  if (isSmScreen.value && props.layoutSm) {
    return props.layoutSm;
  }
  return props.layout;
});

const effectiveAlign = computed(() => {
  if (isSmScreen.value && props.alignSm) {
    return props.alignSm;
  }
  return props.align;
});

const classModifiers = computed(() => [
  ...sizeModifiers.value,
  ...breakpointModifiers.value,
  `--layout-${effectiveLayout.value}`,
  `--align-${effectiveAlign.value}`,
  { "--is-clickable": isClickable.value },
  { "--is-card": isCard.value },
]);

const iconBackgroundClasses = computed(() => [...sizeModifiers.value, ...breakpointModifiers.value]);

const labelClasses = computed(() => [...typographyModifiers.value, ...breakpointModifiers.value]);
</script>

<style>
:host {
  /* Layout */
  --inline-size: var(--sp-comp-tile-inline-size, auto);
  --max-inline-size: var(--sp-comp-tile-max-inline-size, none);
  --gap: var(--sp-comp-tile-gap, var(--sp-ref-spacing-4));
  --gap-sm: var(--sp-comp-tile-gap-sm, var(--sp-ref-spacing-3));

  /* Icon */
  --icon-fill: var(--sp-comp-tile-icon-fill, var(--sp-sys-color-primary));
  --icon-fill-on-bg: var(--sp-comp-tile-icon-fill-on-bg, var(--sp-sys-color-white));

  /* Icon background color */
  --icon-bg-color: var(--sp-comp-tile-icon-bg-color, var(--sp-sys-color-primary));

  /* Icon background sizes */
  --icon-bg-size-micro: var(--sp-comp-tile-icon-bg-size-micro, 1.5rem);
  --icon-bg-size-xxx-small: var(--sp-comp-tile-icon-bg-size-xxx-small, 1.625rem);
  --icon-bg-size-xx-small: var(--sp-comp-tile-icon-bg-size-xx-small, 1.75rem);
  --icon-bg-size-x-small: var(--sp-comp-tile-icon-bg-size-x-small, 2rem);
  --icon-bg-size-small: var(--sp-comp-tile-icon-bg-size-small, 2.5rem);
  --icon-bg-size-medium: var(--sp-comp-tile-icon-bg-size-medium, 3rem);
  --icon-bg-size-large: var(--sp-comp-tile-icon-bg-size-large, 3.5rem);
  --icon-bg-size-x-large: var(--sp-comp-tile-icon-bg-size-x-large, 4.5rem);
  --icon-bg-size-xx-large: var(--sp-comp-tile-icon-bg-size-xx-large, 6rem);
  --icon-bg-size-xxx-large: var(--sp-comp-tile-icon-bg-size-xxx-large, 7rem);
  --icon-bg-size-display: var(--sp-comp-tile-icon-bg-size-display, 8rem);
  --icon-bg-size-x-display: var(--sp-comp-tile-icon-bg-size-x-display, 14rem);

  /* Icon background sizes - mobile (sm) - fallback to desktop values */
  --icon-bg-size-micro-sm: var(--sp-comp-tile-icon-bg-size-micro-sm, var(--icon-bg-size-micro));
  --icon-bg-size-xxx-small-sm: var(--sp-comp-tile-icon-bg-size-xxx-small-sm, var(--icon-bg-size-xxx-small));
  --icon-bg-size-xx-small-sm: var(--sp-comp-tile-icon-bg-size-xx-small-sm, var(--icon-bg-size-xx-small));
  --icon-bg-size-x-small-sm: var(--sp-comp-tile-icon-bg-size-x-small-sm, var(--icon-bg-size-x-small));
  --icon-bg-size-small-sm: var(--sp-comp-tile-icon-bg-size-small-sm, var(--icon-bg-size-small));
  --icon-bg-size-medium-sm: var(--sp-comp-tile-icon-bg-size-medium-sm, var(--icon-bg-size-medium));
  --icon-bg-size-large-sm: var(--sp-comp-tile-icon-bg-size-large-sm, var(--icon-bg-size-large));
  --icon-bg-size-x-large-sm: var(--sp-comp-tile-icon-bg-size-x-large-sm, var(--icon-bg-size-x-large));
  --icon-bg-size-xx-large-sm: var(--sp-comp-tile-icon-bg-size-xx-large-sm, var(--icon-bg-size-xx-large));
  --icon-bg-size-xxx-large-sm: var(--sp-comp-tile-icon-bg-size-xxx-large-sm, var(--icon-bg-size-xxx-large));
  --icon-bg-size-display-sm: var(--sp-comp-tile-icon-bg-size-display-sm, var(--icon-bg-size-display));
  --icon-bg-size-x-display-sm: var(--sp-comp-tile-icon-bg-size-x-display-sm, var(--icon-bg-size-x-display));

  --icon-bg-size: var(--sp-comp-tile-icon-bg-size, var(--icon-bg-size-medium));

  /* Label */
  --label-color: var(--sp-comp-tile-label-color, var(--sp-sys-color-on-surface));
  --label-color-hover: var(
    --sp-comp-tile-label-color-hover,
    var(--sp-comp-tile-label-color, var(--sp-sys-color-on-surface))
  );
  --label-text-decoration: var(--sp-comp-tile-label-text-decoration, none);
  --label-text-decoration-hover: var(
    --sp-comp-tile-label-text-decoration-hover,
    var(--sp-comp-tile-label-text-decoration, none)
  );

  /* Card */
  --card-background: var(--sp-comp-tile-card-background, var(--sp-sys-color-white));
  --card-background-hover: var(
    --sp-comp-tile-card-background-hover,
    var(--sp-comp-tile-card-background, var(--sp-sys-color-white))
  );
  --card-border: var(--sp-comp-tile-card-border, 1px solid var(--sp-sys-color-transparent));
  --card-border-hover: var(--sp-comp-tile-card-border-hover, 1px solid var(--sp-sys-color-outline));
  --card-border-radius: var(--sp-comp-tile-card-border-radius, var(--sp-ref-border-radius-xs));
  --card-shadow: var(--sp-comp-tile-card-shadow, var(--sp-sys-elevation-1));
  --card-shadow-hover: var(--sp-comp-tile-card-shadow-hover, var(--sp-sys-elevation-1));
  --card-padding-block: var(--sp-comp-tile-card-padding-block, var(--sp-ref-spacing-8));
  --card-padding-inline: var(--sp-comp-tile-card-padding-inline, var(--sp-ref-spacing-8));

  /* Content */
  --content-flex: var(--sp-comp-tile-content-flex, 0 1 auto);
  --content-color: var(--sp-comp-tile-content-color, var(--sp-sys-color-on-surface));
  --content-color-hover: var(
    --sp-comp-tile-content-color-hover,
    var(--sp-comp-tile-content-color, var(--sp-sys-color-on-surface))
  );
  --content-text-align: var(--sp-comp-tile-content-text-align, inherit);
  --content-align: var(--sp-comp-tile-content-align, inherit);
  --content-padding: var(--sp-comp-tile-content-padding, 0);
  --content-padding-sm: var(--sp-comp-tile-content-padding-sm, var(--content-padding));

  display: block;
  inline-size: var(--inline-size);
  max-inline-size: var(--max-inline-size);
}
</style>

<style scoped lang="scss">
.sp-tile {
  display: flex;
  gap: var(--gap);
  text-decoration: none;
  color: inherit;
  min-height: var(--sp-tile-group-min-height, 0);
  box-sizing: border-box;

  /* Group --sp-tile-group-connected-* tokens are for connected mode overrides from SpTileGroup */
  &.--is-sm-screen {
    gap: var(--gap-sm);
    min-height: var(--sp-tile-group-min-height-sm, 0);
    padding-block: var(--sp-tile-group-connected-padding-block, 0);
    padding-inline: var(--sp-tile-group-connected-padding-inline, 0);
    --sp-tile-layout-direction: var(--sp-tile-group-connected-layout-direction-sm);
  }

  &.--is-clickable {
    cursor: pointer;

    &:hover,
    &:focus-visible {
      --label-color: var(--label-color-hover);
      --label-text-decoration: var(--label-text-decoration-hover);
    }
  }

  /* Group --sp-tile-group-card-* tokens are for connected mode overrides from SpTileGroup */
  &.--is-card {
    background-color: var(--sp-tile-group-card-background, var(--card-background));
    border: var(--sp-tile-group-card-border, var(--card-border));
    border-radius: var(--sp-tile-group-card-border-radius, var(--card-border-radius));
    box-shadow: var(--sp-tile-group-card-shadow, var(--card-shadow));
    padding-block: var(--sp-tile-group-card-padding-block, var(--card-padding-block));
    padding-inline: var(--sp-tile-group-card-padding-inline, var(--card-padding-inline));

    transition: all var(--sp-ref-transition-fade-duration) var(--sp-ref-transition-fade-easing);

    &.--is-clickable {
      &:hover {
        --card-shadow: var(--sp-tile-group-card-shadow-hover, var(--card-shadow-hover));
        --card-border: var(--sp-tile-group-card-border-hover, var(--card-border-hover));
        --card-background: var(--card-background-hover);
        --content-color: var(--content-color-hover);
      }

      &:focus-visible {
        outline: 1px solid var(--sp-sys-color-primary);
      }
    }
  }

  &.--layout-column {
    flex-direction: var(--sp-tile-layout-direction, column);
  }

  &.--layout-column-reverse {
    flex-direction: var(--sp-tile-layout-direction, column-reverse);
  }

  &.--layout-row {
    flex-direction: var(--sp-tile-layout-direction, row);
  }

  &.--layout-row-reverse {
    flex-direction: var(--sp-tile-layout-direction, row-reverse);
  }

  &.--align-start {
    align-items: flex-start;
    text-align: left;
  }

  &.--align-center {
    align-items: center;
    text-align: center;
  }

  &.--align-end {
    align-items: flex-end;
    text-align: right;
  }

  &__icon-wrapper {
    flex-shrink: 0;

    sp-icon {
      --fill: var(--icon-fill);
    }
  }

  $sizes: (micro, xxx-small, xx-small, x-small, small, medium, large, x-large, xx-large, xxx-large, display, x-display);

  &__icon-background {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    width: var(--icon-bg-size);
    height: var(--icon-bg-size);
    background-color: var(--icon-bg-color);

    sp-icon {
      --fill: var(--icon-fill-on-bg);
    }

    /* Desktop sizes */
    @each $size in $sizes {
      &.--size-#{$size} {
        --icon-bg-size: var(--icon-bg-size-#{$size});
      }
    }

    &.--is-sm-screen {
      /* Mobile sizes: allows clients to define smaller mobile defaults per size */
      @each $size in $sizes {
        &.--size-#{$size} {
          --icon-bg-size: var(--icon-bg-size-#{$size}-sm);
        }
      }

      /* Explicit mobile override via size-sm prop: */
      /* This MUST be a separate loop so ALL these rules come AFTER the mobile sizes loop */
      @each $size in $sizes {
        &.--size-sm-#{$size} {
          --icon-bg-size: var(--icon-bg-size-#{$size}-sm);
        }
      }
    }
  }

  $types: (
    display,
    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    body,
    body-large,
    body-medium,
    body-semi-bold,
    body-bold,
    link,
    small,
    small-bold,
    footnote,
    footnote-medium,
    footnote-bold
  );

  &__label {
    --font: var(--sp-comp-tile-label-font, var(--sp-sys-typescale-body-short));
    font: var(--font);
    color: var(--label-color);
    text-decoration: var(--label-text-decoration);

    &.--is-sm-screen {
      --font: var(--sp-comp-tile-label-font-sm, var(--sp-sys-typescale-body-small-short));
    }

    @each $type in $types {
      &.--type-#{$type} {
        --font: var(--sp-comp-tile-label-font-#{$type}, var(--sp-sys-typescale-#{$type}-short));

        &.--is-sm-screen {
          --font: var(
            --sp-comp-tile-label-font-#{$type}-sm,
            var(--sp-sys-typescale-#{$type}-small-short, var(--sp-sys-typescale-#{$type}-short))
          );
        }
      }
    }
  }

  &__content {
    position: relative;
    display: flex;
    flex-direction: column;
    flex: var(--content-flex);
    color: var(--content-color);
    align-items: var(--content-align);
    text-align: var(--content-text-align);
    padding: var(--content-padding);

    .--is-sm-screen & {
      padding: var(--content-padding-sm);
    }
  }
}
</style>
